import { useTranslation } from 'react-i18next';

import { OrderFormDTO, OrderFormProductDTO, PartyDTO } from 'support/types/dtos';

export type OrderFormValue = {
  quantity: number;
};

type OrderDataParty = {
  name: string | undefined;
  contact?: OrderDataPartyContact;
} & PartyDTO;

type OrderDataPartyContact = {
  first_name: string | undefined;
  email: string | undefined;
};
export type OrderData = {
  products: Record<string, OrderFormValue>;
  parties?: {
    buyer: OrderDataParty | undefined;
    supplier: OrderDataParty | undefined;
    ship_to: OrderDataParty | undefined;
    bill_to?: OrderDataParty | undefined;
  };
  comments?: string;
};
export const getOrderFormSessionKey = (supplierSlug: string): string => `order-form-${supplierSlug}`;

export const createProductsRecord = (orderForm: OrderFormDTO['data'] | undefined) => {
  if (!orderForm) {
    return {};
  }

  return (
    orderForm?.categories
      .flatMap((category) => category.products)
      .reduce<Record<string, OrderFormProductDTO>>((acc, product) => {
        acc[product.id] = product;
        return acc;
      }, {}) ?? {}
  );
};

export function calculateTaxAmountForOrderedProduct(orderedProduct: OrderFormValue, productData: OrderFormProductDTO) {
  const taxRate = productData.tax_rate || 0;
  const subtotal = orderedProduct.quantity * (productData.discounted_unit_price ?? 0);

  return subtotal * (taxRate / 100);
}

export function calculateTotalTax({
  orderedProducts,
  productsData,
}: {
  orderedProducts: Record<string, OrderFormValue>;
  productsData: Record<string, OrderFormProductDTO>;
}): number {
  return Object.entries(orderedProducts).reduce((acc, [productId, ordered]) => {
    const product = productsData[productId];
    if (!product?.tax_rate) {
      return acc;
    }

    const taxAmount = calculateTaxAmountForOrderedProduct(ordered, product);
    acc += taxAmount;

    return acc;
  }, 0);
}

export function isOrderFormWithPrices(
  orderedProducts: Record<string, OrderFormValue>,
  productDataById: Record<string, OrderFormProductDTO>,
): boolean {
  return Object.keys(orderedProducts).some((productId) => {
    const product = productDataById[productId];
    return product && typeof product.discounted_unit_price === 'number';
  });
}

export const useUnitOfMeasureTranslation = ({
  unitOfMeasure,
  format = 'long',
}: {
  unitOfMeasure: OrderFormProductDTO['unit_of_measure'];
  format?: 'long' | 'short';
}) => {
  const { t } = useTranslation();

  /* i18n - For translation key extraction (long format):
    t('orderForms:product.unitOfMeasure.BE')
    t('orderForms:product.unitOfMeasure.BG')
    t('orderForms:product.unitOfMeasure.BJ')
    t('orderForms:product.unitOfMeasure.BO')
    t('orderForms:product.unitOfMeasure.CA')
    t('orderForms:product.unitOfMeasure.CMT')
    t('orderForms:product.unitOfMeasure.COL')
    t('orderForms:product.unitOfMeasure.CQ')
    t('orderForms:product.unitOfMeasure.CS')
    t('orderForms:product.unitOfMeasure.CT')
    t('orderForms:product.unitOfMeasure.CU')
    t('orderForms:product.unitOfMeasure.DAY')
    t('orderForms:product.unitOfMeasure.EA')
    t('orderForms:product.unitOfMeasure.GRM')
    t('orderForms:product.unitOfMeasure.HUR')
    t('orderForms:product.unitOfMeasure.KGM')
    t('orderForms:product.unitOfMeasure.KMT')
    t('orderForms:product.unitOfMeasure.LTR')
    t('orderForms:product.unitOfMeasure.MLT')
    t('orderForms:product.unitOfMeasure.MON')
    t('orderForms:product.unitOfMeasure.MTK')
    t('orderForms:product.unitOfMeasure.MTQ')
    t('orderForms:product.unitOfMeasure.MTR')
    t('orderForms:product.unitOfMeasure.PCK')
    t('orderForms:product.unitOfMeasure.PF')
    t('orderForms:product.unitOfMeasure.PR')
    t('orderForms:product.unitOfMeasure.RO')
    t('orderForms:product.unitOfMeasure.SET')
    t('orderForms:product.unitOfMeasure.TNE')
    t('orderForms:product.unitOfMeasure.TU')
    t('orderForms:product.unitOfMeasure.WEE')

    i18n - For translation key extraction (short format):
    t('orderForms:product.unitOfMeasure.abbreviated.CMT')
    t('orderForms:product.unitOfMeasure.abbreviated.KGM')
    t('orderForms:product.unitOfMeasure.abbreviated.KMT')
    t('orderForms:product.unitOfMeasure.abbreviated.LTR')
    t('orderForms:product.unitOfMeasure.abbreviated.MLT')
    t('orderForms:product.unitOfMeasure.abbreviated.MTK')
    t('orderForms:product.unitOfMeasure.abbreviated.MTQ')
    t('orderForms:product.unitOfMeasure.abbreviated.MTR')
    t('orderForms:product.unitOfMeasure.abbreviated.TNE')
    t('orderForms:product.unitOfMeasure.abbreviated.default')
  */

  const key = unitOfMeasure || 'default';
  const translationPath =
    format === 'short'
      ? `orderForms:product.unitOfMeasure.abbreviated.${key}`
      : `orderForms:product.unitOfMeasure.${key}`;

  return t(translationPath, {
    defaultValue: format === 'short' ? t('orderForms:product.unitOfMeasure.abbreviated.default') : undefined,
  });
};
